import { useAuth } from '@/hooks/useAuth';
import { Navigate } from "react-router";
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireSuperAdmin?: boolean;
  requireOrganizer?: boolean;
}

export const ProtectedRoute = ({ children, requireAdmin = false, requireSuperAdmin = false, requireOrganizer = false }: ProtectedRouteProps) => {
  const { user, isAdmin, isSuperAdmin, isOrganizer, isFeaturedOrganizer, loading } = useAuth();
  const [serverValidated, setServerValidated] = useState(false);
  const [validating, setValidating] = useState(true);

  // Server-side validation for additional security
  useEffect(() => {
    const validateWithServer = async () => {
      if (!user || loading) return;

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          setValidating(false);
          return;
        }

        let requiredRole = null;
        if (requireSuperAdmin) requiredRole = 'superadmin';
        else if (requireAdmin) requiredRole = 'admin';
        else if (requireOrganizer) requiredRole = 'organizer';

        const { data, error } = await supabase.functions.invoke('validate-route', {
          body: { requiredRole },
        });

        if (error) {
          if (import.meta.env.DEV) {
            console.error('Server validation error:', error);
          }
          // SECURITY: do not grant access on validation failure — deny
          toast.error('Unable to verify access. Please try again.');
          setServerValidated(false);
        } else if (data?.authorized === false) {
          toast.error('Access denied');
          setServerValidated(false);
        } else {
          setServerValidated(data?.authorized === true);
        }
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('Server validation failed:', error);
        }
        // SECURITY: deny on error rather than fall back to client-only checks
        setServerValidated(false);
      } finally {
        setValidating(false);
      }
    };

    validateWithServer();
  }, [user, loading, requireAdmin, requireSuperAdmin, requireOrganizer]);

  if (loading || validating) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Client-side checks (primary UX guard)
  if (requireSuperAdmin && !isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  if (requireOrganizer && !isOrganizer && !isFeaturedOrganizer) {
    return <Navigate to="/" replace />;
  }

  // Server validation failed (additional security layer)
  if (!serverValidated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};
